import EspTypePicker from "./EspTypePicker";
import {useEffect, useState} from "react";
import {Button, Input, InputGroup, Loader, Modal, Table, Tabs, toaster, Toggle, Uploader} from "rsuite";
import Tab from "rsuite/Tab";
import {ArtifactsApi} from "../../../services/Artifacts.api";
import {
  actionConfigs,
  ClockStationFirmwareFormState,
  ClockStationFirmwareModalProps,
  FirmwareHistoryItem,
  FirmwareModalTab,
} from "../Firmwares.model";
import NotificationFactory from "../../../shared/Notifications";
import {FirmwaresUtils} from "../Firmwares.util";
import {TfiClipIcon} from "../../../models/Icons.model";

const {Column, HeaderCell, Cell} = Table;
const ClockStationFirmwareModal = ({open, mode, firmware, onClose, onSuccess}: ClockStationFirmwareModalProps) => {
  const [form, setForm] = useState<ClockStationFirmwareFormState>(() => FirmwaresUtils.getClockStationFirmwareInitialState(firmware));
  const [modalFirmware, setModalFirmware] = useState(firmware);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [isVersionLocked, setIsVersionLocked] = useState(false);
  const [isModelLocked, setIsModelLocked] = useState(false);
  const [isModelTypeLocked, setIsModelTypeLocked] = useState(false);
  const [isChannelsLocked, setIsChannelsLocked] = useState(false);
  const [activeTab, setActiveTab] = useState<FirmwareModalTab>("general");
  const [historyItems, setHistoryItems] = useState<FirmwareHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setModalFirmware(null);
      return;
    }

    setModalFirmware(firmware);
    setForm(FirmwaresUtils.getClockStationFirmwareInitialState(firmware));
    setFileList([]);
    setUploaderKey(0);
    setIsVersionLocked(false);
    setIsModelLocked(false);
    setIsModelTypeLocked(false);
    setIsChannelsLocked(false);
    setDeletePending(false);
    setActiveTab("general");
    setHistoryItems([]);
    setHistoryLoaded(false);

    if (mode !== "edit" || !firmware?.id) {
      setDetailsLoading(false);
      return;
    }

    let isActual = true;

    setDetailsLoading(true);

    ArtifactsApi.clockStationFirmwares.getById(firmware.id)
      .then((response) => {
        if (!isActual) return;

        const detailedFirmware = response as ClockStationFirmwareModalProps["firmware"];

        setModalFirmware(detailedFirmware);
        setForm(FirmwaresUtils.getClockStationFirmwareInitialState(detailedFirmware));
      })
      .catch((error) => {
        if (!isActual) return;

        console.error("Ошибка получения информации о прошивке часовой станции:", error);
        toaster.push(NotificationFactory.error("Ошибка получения информации о прошивке"), {placement: "topEnd"});
      })
      .finally(() => {
        if (isActual) {
          setDetailsLoading(false);
        }
      });

    return () => {
      isActual = false;
    };
  }, [open, firmware, mode]);

  const currentFirmware = modalFirmware || firmware;

  const loadHistory = async () => {
    if (!currentFirmware?.id || historyLoaded || historyLoading) return;

    setHistoryLoading(true);

    try {
      const response = await ArtifactsApi.clockStationFirmwares.getHistory(currentFirmware.id);

      setHistoryItems(FirmwaresUtils.getItems<FirmwareHistoryItem>(response));
      setHistoryLoaded(true);
    } catch (error) {
      console.error("Ошибка получения истории изменений прошивки часовой станции:", error);
      toaster.push(NotificationFactory.error("Ошибка получения истории изменений"), {placement: "topEnd"});
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      loadHistory().then();
    }
  }, [activeTab]);

  const handleChange = <K extends keyof ClockStationFirmwareFormState>(field: K, value: ClockStationFirmwareFormState[K]) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const selectedFile = fileList[0];
  const selectedFileSize = selectedFile?.blobFile?.size;
  const selectedFileSizeMb = typeof selectedFileSize === "number"
    ? `${(selectedFileSize / (1024 * 1024)).toFixed(2)} MB`
    : null;

  const handleFileChange = async (files: any[]) => {
    setFileList(files);
    const file = files[0]?.blobFile || null;

    handleChange("file", file);

    if (!file) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      version: "",
      description: "",
      model: "",
      modelType: null,
      channels: null,
      melody: null,
      customMelodyEffect: null,
      file,
    }));
    setIsVersionLocked(false);
    setIsModelLocked(false);
    setIsModelTypeLocked(false);
    setIsChannelsLocked(false);

    setParseLoading(true);

    try {
      const response = await ArtifactsApi.clockStationFirmwares.parse(file);
      const parsedVersion = response?.version?.trim() || "";
      const parsedModel = response?.model?.trim() || "";
      const parsedModelType = response?.modelType ? FirmwaresUtils.getEspType(response.modelType) : null;
      const parsedChannels = typeof response?.channels === "number" ? response.channels : null;
      const parsedMelody = typeof response?.melody === "boolean" ? response.melody : null;
      const parsedCustomMelodyEffect = typeof response?.customMelodyEffect === "boolean" ? response.customMelodyEffect : null;

      if (parsedVersion) {
        handleChange("version", parsedVersion);
        setIsVersionLocked(true);
      } else {
        setIsVersionLocked(false);
      }

      if (parsedModel) {
        handleChange("model", parsedModel);
        setIsModelLocked(true);
      } else {
        setIsModelLocked(false);
      }

      if (parsedModelType) {
        handleChange("modelType", parsedModelType);
        setIsModelTypeLocked(true);
      } else {
        setIsModelTypeLocked(false);
      }

      if (parsedChannels !== null) {
        handleChange("channels", parsedChannels);
        setIsChannelsLocked(true);
      } else {
        setIsChannelsLocked(false);
      }

      if (parsedMelody !== null) {
        handleChange("melody", parsedMelody);
      }

      if (parsedCustomMelodyEffect !== null) {
        handleChange("customMelodyEffect", parsedCustomMelodyEffect);
      }
    } catch (error) {
      console.error("Ошибка разбора прошивки часовой станции:", error);
      setIsVersionLocked(false);
      setIsModelLocked(false);
      setIsModelTypeLocked(false);
      setIsChannelsLocked(false);
      toaster.push(NotificationFactory.error("Не удалось определить параметры прошивки из файла"), {placement: "topEnd"});
    } finally {
      setParseLoading(false);
    }
  };

  const handleFileRemove = () => {
    setFileList([]);
    setUploaderKey((prev) => prev + 1);
    setIsVersionLocked(false);
    setIsModelLocked(false);
    setIsModelTypeLocked(false);
    setIsChannelsLocked(false);
    setForm((prev) => ({
      ...prev,
      version: "",
      description: "",
      model: "",
      modelType: null,
      channels: null,
      melody: null,
      customMelodyEffect: null,
      file: null,
    }));
  };

  const handleTabSelect = (eventKey?: string | number) => {
    setActiveTab(eventKey === "history" ? "history" : "general");
  };

  const handleSubmit = async () => {
    const missingFields = FirmwaresUtils.getMissingRequiredFields([
      {label: "Версия", value: form.version},
      {label: "Описание", value: form.description},
      {label: "Модель", value: form.model},
      {label: "Тип ESP", value: form.modelType},
      {label: "Каналы", value: form.channels},
      {label: "Музыка", value: form.melody},
      {label: "Уникальная музыка", value: form.customMelodyEffect},
    ]);

    if (missingFields.length) {
      toaster.push(NotificationFactory.error(FirmwaresUtils.getRequiredFieldsMessage(missingFields)), {placement: "topEnd"});
      return;
    }

    setLoading(true);

    try {
      const payload = {
        version: form.version || null,
        description: form.description || null,
        model: form.model || null,
        modelType: form.modelType || undefined,
        channels: form.channels ?? undefined,
        melody: form.melody ?? undefined,
        customMelodyEffect: form.customMelodyEffect ?? undefined,
      };

      if (mode === "create") {
        await ArtifactsApi.clockStationFirmwares.create({
          ...payload,
          file: form.file,
        });
      } else if (currentFirmware?.id) {
        await ArtifactsApi.clockStationFirmwares.update(currentFirmware.id, payload);

        if (form.file) {
          await ArtifactsApi.clockStationFirmwares.updateFile(currentFirmware.id, form.file);
        }
      }

      toaster.push(NotificationFactory.success(FirmwaresUtils.getSuccessMessage(mode)), {placement: "topEnd"});
      onSuccess();
    } catch (error) {
      console.error("Ошибка сохранения прошивки часовой станции:", error);
      toaster.push(NotificationFactory.error(FirmwaresUtils.getErrorMessage(error, mode)), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!currentFirmware?.id) return;

    setDownloadLoading(true);

    try {
      const blob = await ArtifactsApi.clockStationFirmwares.download(currentFirmware.id);
      const fileName = currentFirmware.fileDescriptor?.name || `${currentFirmware.version || "clock-station-firmware"}.hex`;

      FirmwaresUtils.downloadBlob(blob, fileName);
    } catch (error) {
      console.error("Ошибка скачивания файла прошивки часовой станции:", error);
      toaster.push(NotificationFactory.error("Ошибка скачивания файла прошивки"), {placement: "topEnd"});
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentFirmware?.id) return;

    setDeleteLoading(true);

    try {
      await ArtifactsApi.clockStationFirmwares.delete(currentFirmware.id);
      toaster.push(NotificationFactory.success("Прошивка удалена"), {placement: "topEnd"});
      setDeletePending(false);
      onSuccess();
    } catch (error) {
      console.error("Ошибка удаления прошивки часовой станции:", error);
      toaster.push(NotificationFactory.error(FirmwaresUtils.getErrorMessage(error, "edit")), {placement: "topEnd"});
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderHistory = () => {
    if (mode === "create") {
      return <div className="firmware-modal__history-empty">История появится после создания прошивки</div>;
    }

    if (historyLoading) {
      return (
        <div className="firmware-modal__history-loader">
          <Loader content="Загрузка истории..." />
        </div>
      );
    }

    if (!historyItems.length) {
      return <div className="firmware-modal__history-empty">История изменений пуста</div>;
    }

    return (
      <Table autoHeight data={historyItems} rowHeight={60} className="firmware-modal__history-table" wordWrap>
        <Column width={120} verticalAlign="middle" fullText>
          <HeaderCell>Действие</HeaderCell>
          <Cell dataKey="action">
            {(rowData: FirmwareHistoryItem) => rowData.action || "-"}
          </Cell>
        </Column>

        <Column flexGrow={1} minWidth={220} verticalAlign="middle" fullText>
          <HeaderCell>Комментарий</HeaderCell>
          <Cell dataKey="comment">
            {(rowData: FirmwareHistoryItem) => rowData.comment || "-"}
          </Cell>
        </Column>

        <Column width={240} verticalAlign="middle" fullText>
          <HeaderCell>Автор</HeaderCell>
          <Cell dataKey="performedBy">
            {(rowData: FirmwareHistoryItem) => rowData.performedBy || "-"}
          </Cell>
        </Column>

        <Column width={150} verticalAlign="middle" fullText>
          <HeaderCell>Дата</HeaderCell>
          <Cell dataKey="createdAt">
            {(rowData: FirmwareHistoryItem) => FirmwaresUtils.formatDate(rowData.createdAt)}
          </Cell>
        </Column>
      </Table>
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose} size="md" backdrop="static">
        <Modal.Header>
          <Modal.Title>{FirmwaresUtils.getModalTitle(mode)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={handleTabSelect}>
            <Tab eventKey="general" title="Общее">
              <div className="mb-3 firmware-modal__selected-file-row">
                <Uploader
                  key={`clock-station-firmware-upload-${uploaderKey}`}
                  accept=".hex"
                  fileList={fileList}
                  fileListVisible={false}
                  autoUpload={false}
                  multiple={false}
                  listType="picture-text"
                  onChange={(files) => {
                    void handleFileChange(files);
                  }}
                  action=""
                >
                  <Button color="blue" appearance="primary">
                    {mode === "edit" ? "Обновить файл" : "Добавить файл"}
                  </Button>
                </Uploader>

                {selectedFile && (
                  <div className="firmware-modal__selected-file">
                    <div className="firmware-modal__selected-file-icon">
                      <TfiClipIcon />
                    </div>
                    <div className="firmware-modal__selected-file-info">
                      <div className="firmware-modal__selected-file-name">
                        {selectedFile.name || "Файл выбран"}
                        {selectedFileSizeMb && (
                          <span className="firmware-modal__selected-file-size ms-2">{selectedFileSizeMb}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="firmware-modal__selected-file-remove"
                      onClick={handleFileRemove}
                      aria-label="Удалить выбранный файл"
                    >
                      ×
                    </button>
                  </div>
                )}

                {parseLoading && <Loader content="Определение версии..." />}
              </div>

              <div className="firmware-modal__clockstation-top-row mb-3">
                <div>
                  <InputGroup>
                    <InputGroup.Addon>Версия</InputGroup.Addon>
                    <Input
                      disabled={isVersionLocked}
                      value={form.version ?? ""}
                      onChange={(value) => handleChange("version", value)}
                    />
                  </InputGroup>
                </div>

                <div>
                  <InputGroup>
                    <InputGroup.Addon>Модель</InputGroup.Addon>
                    <Input
                      disabled={isModelLocked}
                      value={form.model}
                      onChange={(value) => handleChange("model", value)}
                    />
                  </InputGroup>
                </div>

                <div>
                  <InputGroup>
                    <InputGroup.Addon>Каналы</InputGroup.Addon>
                    <Input
                      type="number"
                      disabled={isChannelsLocked}
                      value={form.channels ?? ""}
                      onChange={(value) => handleChange("channels", value === "" ? null : Number(value))}
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="mb-3">
                <EspTypePicker
                  value={form.modelType}
                  disabled={isModelTypeLocked}
                  onChange={(value) => handleChange("modelType", value)}
                />
              </div>

              <div className="firmware-modal__clockstation-toggle-row mb-3">
                <div>
                  <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 h-100">
                    <span>Музыка</span>
                    <Toggle
                      color="blue"
                      checkedChildren="Да"
                      unCheckedChildren="Нет"
                      checked={Boolean(form.melody)}
                      onChange={(checked: boolean) => handleChange("melody", checked)}
                    />
                  </div>
                </div>

                <div>
                  <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 h-100">
                    <span>Уникальная музыка</span>
                    <Toggle
                      color="blue"
                      checkedChildren="Да"
                      unCheckedChildren="Нет"
                      checked={Boolean(form.customMelodyEffect)}
                      onChange={(checked: boolean) => handleChange("customMelodyEffect", checked)}
                    />
                  </div>
                </div>
              </div>

              <InputGroup className="mb-3">
                <Input
                  as="textarea"
                  rows={4}
                  value={form.description}
                  placeholder="Описание"
                  onChange={(value) => handleChange("description", value)}
                />
              </InputGroup>

              <div className="d-flex align-items-start flex-wrap gap-2">
                <div className="firmware-modal__file-actions">
                  {mode === "edit" && (
                    <Button
                      appearance="primary"
                      color="blue"
                      className="firmware-modal__download-button"
                      loading={downloadLoading}
                      disabled={loading || detailsLoading || deleteLoading || downloadLoading || !currentFirmware?.id || !currentFirmware?.fileDescriptor?.name}
                      onClick={handleDownload}
                    >
                      Скачать
                    </Button>
                  )}
                </div>

                {mode === "edit" && (
                  <Button
                    appearance="primary"
                    color="red"
                    className="firmware-modal__action-button"
                    disabled={loading || detailsLoading || deleteLoading || !currentFirmware?.id}
                    onClick={() => setDeletePending(true)}
                  >
                    Удалить
                  </Button>
                )}
              </div>
            </Tab>

            <Tab eventKey="history" title="История изменений">
              {renderHistory()}
            </Tab>
          </Tabs>
        </Modal.Body>
        {activeTab === "general" && (
          <Modal.Footer>
            <Button appearance="primary" onClick={handleSubmit} loading={loading} disabled={loading || detailsLoading || deleteLoading || parseLoading}>
              Сохранить
            </Button>
            <Button appearance="subtle" onClick={onClose} disabled={loading || deleteLoading}>
              Отмена
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal open={deletePending} onClose={() => setDeletePending(false)} backdrop="static" size="xs">
        <Modal.Header>
          <Modal.Title>{actionConfigs.delete.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{actionConfigs.delete.message}</Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" onClick={() => setDeletePending(false)} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button
            appearance="primary"
            color={actionConfigs.delete.color}
            onClick={handleDelete}
            loading={deleteLoading}
            disabled={deleteLoading}
          >
            {actionConfigs.delete.confirmText}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ClockStationFirmwareModal;
